import type { CountryCode } from "../data";

export const SLUG_TO_CODE: Record<string, CountryCode> = {
  australia: "au",
  "united-states": "us",
  "united-kingdom": "uk",
  canada: "ca",
};

export const CODE_TO_SLUG: Record<CountryCode, string> = {
  au: "australia",
  us: "united-states",
  uk: "united-kingdom",
  ca: "canada",
};

export type PonziCategory = "pension" | "housing" | "education" | "wages";

export function countryFromPath(pathname: string = window.location.pathname): CountryCode {
  const m = pathname.match(/\/(?:pension|housing|education|wages)-ponzi\/([^/]+)/);
  const slug = m?.[1]?.toLowerCase();
  return (slug && SLUG_TO_CODE[slug]) || "us";
}

export function sectionFromPath(
  pathname: string = window.location.pathname,
): "pension" | "housing" | "education" | "wages" | "landing" {
  if (pathname.startsWith("/pension-ponzi")) return "pension";
  if (pathname.startsWith("/housing-ponzi")) return "housing";
  if (pathname.startsWith("/education-ponzi")) return "education";
  if (pathname.startsWith("/wages-ponzi")) return "wages";
  return "landing";
}

export function cohortYearFromQuery(): number | null {
  const v = new URLSearchParams(window.location.search).get("cohort");
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export function boughtYearFromQuery(): number | null {
  const v = new URLSearchParams(window.location.search).get("bought");
  if (!v) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

interface CrossLinkOpts {
  cohort?: number | null;
  bought?: number | null;
}

/**
 * Canonical builder for cross-category links. Returns
 *   /{category}-ponzi/{country-slug}?cohort=YYYY[&bought=YYYY]
 *
 * The `bought` param is appended whenever provided (caller decides). For
 * pension / education / wages pages it should typically be omitted; housing
 * is the main consumer.
 */
export function buildPonziHref(
  category: PonziCategory,
  country: CountryCode,
  opts: CrossLinkOpts = {},
): string {
  const slug = CODE_TO_SLUG[country];
  const params = new URLSearchParams();
  if (opts.cohort != null && Number.isFinite(opts.cohort)) {
    params.set("cohort", String(opts.cohort));
  }
  if (opts.bought != null && Number.isFinite(opts.bought)) {
    params.set("bought", String(opts.bought));
  }
  const qs = params.toString();
  return `/${category}-ponzi/${slug}${qs ? `?${qs}` : ""}`;
}

/** Landing page link, optionally preserving cohort. */
export function buildLandingHref(opts: { cohort?: number | null } = {}): string {
  if (opts.cohort != null && Number.isFinite(opts.cohort)) {
    return `/?cohort=${opts.cohort}`;
  }
  return "/";
}

/**
 * Read the current cross-link state from the URL with sensible defaults
 * (`cohort` falls back to the canonical 1955 default if absent / invalid).
 */
export function readCrossLinkState(): { cohort: number; bought: number | null } {
  const cohort = cohortYearFromQuery() ?? 1955;
  const bought = boughtYearFromQuery();
  return { cohort, bought };
}
